/**
 * Helpers for the "request support for a new platform" dialog.
 */

// extensions accepted by the backend (json is intentionally not supported)
const ALLOWED_EXTENSIONS = [
  "csv",
  "tsv",
  "xlsx",
  "xls",
  "pdf",
  "png",
  "jpg",
  "jpeg",
];
// files which cannot be processed automatically - they are reviewed manually
const NON_PROCESSABLE_EXTENSIONS = ["pdf", "png", "jpg", "jpeg"];

const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024;

// number of placeholder values generated for each dimension in the preview
const DIMENSION_VALUE_COUNT = 2;
// maximum number of dimensions of one report type (enforced by the backend as well)
const MAX_DIMENSIONS = 7;
// limits to keep the amount of generated preview rows reasonable
const MAX_PREVIEW_DIMENSIONS = 5;
const PREVIEW_MONTHS = 12;
// keys of the preview rows which must not be overwritten by a dimension
const RESERVED_ROW_KEYS = ["date", "metric", "count"];

function fileExtension(name) {
  const parts = String(name || "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
}

function isAllowedFile(file) {
  return ALLOWED_EXTENSIONS.includes(fileExtension(file.name));
}

function isNonProcessableFile(file) {
  return NON_PROCESSABLE_EXTENSIONS.includes(fileExtension(file.name));
}

/** Host of the url without `www.` (lower case), empty string if it cannot be parsed. */
function normalizeHost(url) {
  if (!url) return "";
  const text = String(url).trim();
  try {
    const parsed = new URL(
      /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`,
    );
    return parsed.hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

function includesText(haystack, needle) {
  return (
    !!haystack &&
    !!needle &&
    haystack.toLowerCase().includes(needle.toLowerCase())
  );
}

/**
 * Finds platforms which look like the one the user describes.
 *
 * @param platforms - platforms as returned by the `all-platform` API
 * @param {{name: string, url: string, provider: string}} query
 * @returns platforms with an added `matchReasons` array, the best matches first
 */
function findSimilarPlatforms(platforms, { name, url, provider }, limit = 10) {
  const nameQuery = (name || "").trim();
  const providerQuery = (provider || "").trim();
  const host = normalizeHost(url);
  const out = [];
  for (const platform of platforms || []) {
    const reasons = [];
    if (
      nameQuery &&
      (includesText(platform.name, nameQuery) ||
        includesText(platform.short_name, nameQuery) ||
        includesText(nameQuery, platform.short_name))
    ) {
      reasons.push("name");
    }
    if (host && normalizeHost(platform.url) === host) {
      reasons.push("url");
    }
    if (providerQuery && includesText(platform.provider, providerQuery)) {
      reasons.push("provider");
    }
    if (reasons.length) out.push({ ...platform, matchReasons: reasons });
  }
  out.sort(
    (a, b) =>
      b.matchReasons.length - a.matchReasons.length ||
      String(a.name).localeCompare(String(b.name)),
  );
  return out.slice(0, limit);
}

/** Placeholder values for a dimension, e.g. `Department_01`, `Department_02`. */
function dimensionValues(name, count = DIMENSION_VALUE_COUNT) {
  const base = (name || "").trim() || "Dimension";
  return Array.from(
    { length: count },
    (_, i) => `${base}_${String(i + 1).padStart(2, "0")}`,
  );
}

/** Key under which the dimension is stored in a preview row. */
function dimensionRowKey(name) {
  const base = (name || "").trim() || "Dimension";
  return RESERVED_ROW_KEYS.includes(base.toLowerCase()) ? `${base}_` : base;
}

function lastMonths(count, now = new Date()) {
  const out = [];
  for (let i = count; i >= 1; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`,
    );
  }
  return out;
}

/** Cartesian product of arrays of values -> array of arrays. */
function cartesian(lists) {
  return lists.reduce(
    (acc, list) => acc.flatMap((prefix) => list.map((v) => [...prefix, v])),
    [[]],
  );
}

/**
 * Generates random data for the preview.
 *
 * Each row has the shape consumed by APIChart: `{date, metric, <dimension>..., count}`.
 * There is a row for each month of the last year, each metric and each combination of
 * the generated dimension values.
 *
 * @param {{name: string}[]} metrics
 * @param {{name: string}[]} dimensions
 * @param {function} random - source of randomness (injectable for tests)
 */
function generatePreviewRows(
  metrics,
  dimensions,
  { random = Math.random, now = new Date() } = {},
) {
  const metricNames = metrics.map(
    (m, i) =>
      (m.name || "").trim() || `Metric_${String(i + 1).padStart(2, "0")}`,
  );
  const dims = dimensions.slice(0, MAX_PREVIEW_DIMENSIONS).map((d) => ({
    key: dimensionRowKey(d.name),
    values: dimensionValues(d.name),
  }));
  const combinations = cartesian(dims.map((d) => d.values));
  const months = lastMonths(PREVIEW_MONTHS, now);

  const rows = [];
  for (const metric of metricNames) {
    // each metric has its own scale and a slight trend, so that the chart is not flat
    const scale = 50 + Math.floor(random() * 450);
    for (const [monthIndex, date] of months.entries()) {
      for (const combination of combinations) {
        const row = { date, metric };
        dims.forEach((d, i) => {
          row[d.key] = combination[i];
        });
        row.count = Math.round(
          scale * (0.6 + random() * 0.8) * (1 + monthIndex / 24),
        );
        rows.push(row);
      }
    }
  }
  return rows;
}

/** Keys of the dimensions in the preview rows (same order as the dimensions). */
function previewDimensionKeys(dimensions) {
  return dimensions
    .slice(0, MAX_PREVIEW_DIMENSIONS)
    .map((d) => dimensionRowKey(d.name));
}

/** Names are compared case-insensitively and ignoring surrounding whitespace. */
function normalizeName(name) {
  return (name || "").trim().toLowerCase();
}

/** Is the name (non-empty) used by another item of the list than the one at `index`? */
function isDuplicateName(items, index) {
  const name = normalizeName(items[index].name);
  return (
    !!name &&
    items.some((x, i) => i !== index && normalizeName(x.name) === name)
  );
}

function hasDuplicateNames(items) {
  return items.some((_, i) => isDuplicateName(items, i));
}

/**
 * Has the report type everything the form requires?
 * (files, metrics and dimensions with names)
 */
function isReportTypeComplete(rt) {
  return (
    rt.files.length > 0 &&
    rt.metrics.length > 0 &&
    rt.metrics.every((m) => m.name.trim()) &&
    rt.dimensions.every((d) => d.name.trim()) &&
    rt.dimensions.length <= MAX_DIMENSIONS &&
    !hasDuplicateNames(rt.metrics) &&
    !hasDuplicateNames(rt.dimensions)
  );
}

/**
 * Shortens a long file name in the middle, so that the beginning and the extension
 * stay visible, e.g. `very_long_report_na…2024_01.xlsx`.
 */
function shortenFileName(name, maxLength = 40) {
  if (name.length <= maxLength) return name;
  const dot = name.lastIndexOf(".");
  // the extension is kept (unless it is suspiciously long) with some of the name before it
  const extension = dot > 0 && name.length - dot <= 6 ? name.slice(dot) : "";
  const tail = extension ? 8 + extension.length : 10;
  const head = maxLength - tail - 1;
  return `${name.slice(0, head)}…${name.slice(name.length - tail)}`;
}

/** Human readable size of a file, e.g. `1.2 MB`. */
function formatFileSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/** The backend requires a scheme in the URL, but users tend to omit it. */
function normalizeUrl(url) {
  const text = (url || "").trim();
  if (!text) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`;
}

// the same hostnames as the backend accepts: localhost, a domain with an alphabetic top level
// domain, or an IPv4 address
const OCTET = "(?:25[0-5]|2[0-4]\\d|1?\\d?\\d)";
const HOSTNAME_REGEXP = new RegExp(
  "^(?:localhost" +
    "|(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.)+(?:[a-z]{2,63}|xn--[a-z0-9]{1,59})" +
    `|${OCTET}(?:\\.${OCTET}){3})$`,
  "i",
);

/**
 * Is the text empty or a valid http(s) URL? (the scheme may be omitted, see `normalizeUrl`)
 * It is as strict as the backend, which would reject an invalid URL.
 */
function isValidUrl(text) {
  const value = (text || "").trim();
  if (!value) return true;
  if (/\s/.test(value)) return false;
  try {
    const url = new URL(normalizeUrl(value));
    return (
      ["http:", "https:"].includes(url.protocol) &&
      HOSTNAME_REGEXP.test(url.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * The platform data the request carries: when an existing platform is selected, its
 * name, URL and provider are used, otherwise the ones entered by the user.
 *
 * @param form - the form of the dialog, `existing_platform_data` holds
 *   `{name, url, provider}` of the selected existing platform
 */
function requestedPlatform(form) {
  const existing = form.existing_platform_data;
  if (form.existing_platform !== null && existing) {
    return {
      platform_name: (existing.name || "").trim(),
      platform_url: normalizeUrl(existing.url),
      provider: (existing.provider || "").trim(),
    };
  }
  return {
    platform_name: form.platform_name.trim(),
    platform_url: normalizeUrl(form.platform_url),
    provider: form.provider.trim(),
  };
}

export {
  ALLOWED_EXTENSIONS,
  DEFAULT_MAX_FILE_SIZE,
  MAX_DIMENSIONS,
  MAX_PREVIEW_DIMENSIONS,
  NON_PROCESSABLE_EXTENSIONS,
  dimensionRowKey,
  dimensionValues,
  fileExtension,
  hasDuplicateNames,
  isDuplicateName,
  findSimilarPlatforms,
  formatFileSize,
  generatePreviewRows,
  shortenFileName,
  isAllowedFile,
  isNonProcessableFile,
  isReportTypeComplete,
  isValidUrl,
  normalizeHost,
  previewDimensionKeys,
  requestedPlatform,
};
